import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "@/api/person/queries";
import {
  useAuthorships,
  useCourseCertificates,
  useDispensations,
  useInsurancePolicies,
  useReductions,
  useLegalAids,
  useTeachings,
} from "@/hooks/cle-hooks";
import { usePersonPaabudList } from "@/hooks/person-hooks/usePersonPaabud";
import { usePersonPaalaegList } from "@/hooks/person-hooks/usePersonPaalaeg";
import usePersonChecks from "@/hooks/errorHandling/usePersonChecks";
import type { PersonDto } from "@/interfaces/schema";
import { transformQueryDataPerioder } from "@/util/apollo";
import { useMemo, useState } from "react";

import { AuthorshipReviewView } from "@/components/pages/efteruddannelse/AuthorshipReviewView";
import { InsurancePolicyReviewView } from "@/components/pages/efteruddannelse/InsurancePolicyReviewView";
import { LegalAidReviewView } from "@/components/pages/efteruddannelse/LegalAidReviewView";
import { ReductionReviewView } from "@/components/pages/efteruddannelse/ReductionReviewView";
import { TeachingReviewView } from "@/components/pages/efteruddannelse/TeachingReviewView";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../accordion/Accordion";
import { AuthorshipsTable } from "./Efteruddannelse/AuthorshipsTable";
import { CourseCertificatesTable } from "./Efteruddannelse/CourseCertificatesTable";
import { CourseCertificateReviewView } from "./Efteruddannelse/CourseCertificateReviewView";
import {
  decoratePersonEfteruddannelseList,
  type DecoratedPersonEfteruddannelse,
} from "./Efteruddannelse/decoratePeriod";
import { CreateDispensationModal } from "./Efteruddannelse/CreateDispensationModal";
import { buildPeriodLabelLookup } from "./Efteruddannelse/dispensationUtils";
import { DispensationDetailView } from "./Efteruddannelse/DispensationDetailView";
import { DispensationerTable } from "./Efteruddannelse/DispensationerTable";
import { EfteruddannelsePeriodDetailView } from "./Efteruddannelse/EfteruddannelsePeriodDetailView";
import {
  AUTHORSHIPS_PAGE_SIZE,
  COURSE_CERTIFICATES_PAGE_SIZE,
  DISPENSATIONS_PAGE_SIZE,
  documentCategories,
  INSURANCE_POLICIES_PAGE_SIZE,
  REDUCTIONS_PAGE_SIZE,
  LEGAL_AID_PAGE_SIZE,
  TEACHINGS_PAGE_SIZE,
} from "./Efteruddannelse/mockData";
import { useDocumentPagination } from "./Efteruddannelse/useDocumentPagination";
import { CreatePaabudModal } from "./Efteruddannelse/CreatePaabudModal";
import { CreatePaalaegModal } from "./Efteruddannelse/CreatePaalaegModal";
import { filterVisiblePaabud } from "./Efteruddannelse/paabudUtils";
import { filterVisiblePaalaeg } from "./Efteruddannelse/paalaegUtils";
import { PaabudDetailView } from "./Efteruddannelse/PaabudDetailView";
import { PaabudTable } from "./Efteruddannelse/PaabudTable";
import { PaalaegDetailView } from "./Efteruddannelse/PaalaegDetailView";
import { PaalaegTable } from "./Efteruddannelse/PaalaegTable";
import { PeriodsTable } from "./Efteruddannelse/PeriodsTable";
import { InsurancePoliciesTable } from "./Efteruddannelse/InsurancePoliciesTable";
import { ReductionsTable } from "./Efteruddannelse/ReductionsTable";
import { LegalAidTable } from "./Efteruddannelse/LegalAidTable";
import { TeachingsTable } from "./Efteruddannelse/TeachingsTable";

export default function Efteruddannelse({ personId }: { personId: string }) {
  const userId = personId;
  const [selectedCourseCertificateId, setSelectedCourseCertificateId] =
    useState<string | undefined>(undefined);
  const [selectedReductionId, setSelectedReductionId] = useState<
    string | undefined
  >(undefined);
  const [selectedLegalAidId, setSelectedLegalAidId] = useState<
    string | undefined
  >(undefined);
  const [selectedAuthorshipId, setSelectedAuthorshipId] = useState<
    string | undefined
  >(undefined);
  const [selectedTeachingId, setSelectedTeachingId] = useState<
    string | undefined
  >(undefined);
  const [selectedInsurancePolicyId, setSelectedInsurancePolicyId] = useState<
    string | undefined
  >(undefined);
  // Perioden holdes som id og udledes af decoratedPeriods nedenfor. Holdt vi objektet, ville
  // detaljevisningen blive ved med at vise et forældet snapshot efter en refetch - og en gemt
  // rettelse ville se ud som om den ikke virkede.
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | undefined>(
    undefined,
  );
  const [selectedPaabudId, setSelectedPaabudId] = useState<string | undefined>(
    undefined,
  );
  const [selectedPaalaegId, setSelectedPaalaegId] = useState<
    string | undefined
  >(undefined);
  const [selectedDispensationId, setSelectedDispensationId] = useState<
    string | undefined
  >(undefined);

  const { loading, error, data, refetch } = useQuery<{ person: PersonDto }>(
    PERSON_BY_ID_QUERY("personByIdEfteruddannelseTab", {
      personEfteruddannelse: true,
    }),
    {
      notifyOnNetworkStatusChange: true,
      variables: { id: personId },
    },
  );

  const transformedData = useMemo(
    () => (data ? transformQueryDataPerioder(data) : undefined),
    [data],
  );
  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    transformedData,
  );

  const decoratedPeriods = useMemo(
    () => decoratePersonEfteruddannelseList(person?.personEfteruddannelse),
    [person?.personEfteruddannelse],
  );

  const periodeIntervaller = useMemo(
    () =>
      decoratedPeriods.map((p) => ({
        fraDato: p.periode.fraDato,
        tilDato: p.periode.tilDato,
      })),
    [decoratedPeriods],
  );

  const selectedPeriod = useMemo(
    () => decoratedPeriods.find((p) => p.id === selectedPeriodId),
    [decoratedPeriods, selectedPeriodId],
  );

  /**
   * Perioden umiddelbart FØR den valgte, i samme kronologiske orden som backenden bruger. Overførte
   * lektioner kommer derfra, så uden en foregående periode er der intet at overføre (backenden
   * svarer 400), og det er dens indberettede lektioner 54-reglen måles på.
   */
  const forrigePeriode = useMemo(() => {
    if (!selectedPeriodId) return undefined;
    const kronologisk = [...decoratedPeriods].sort(
      (a, b) =>
        new Date(a.periode.fraDato).getTime() -
        new Date(b.periode.fraDato).getTime(),
    );
    const index = kronologisk.findIndex((p) => p.id === selectedPeriodId);
    return index > 0 ? kronologisk[index - 1] : undefined;
  }, [decoratedPeriods, selectedPeriodId]);

  // Én sidetilstand pr. dokumentliste. Sidetallet skal ligge her, hvor listekaldene sker -
  // det var som hardkodet konstant, så pilene i tabellerne aldrig kunne flytte siden.
  const courseCertificatesPaging = useDocumentPagination(
    COURSE_CERTIFICATES_PAGE_SIZE,
  );
  const reductionsPaging = useDocumentPagination(REDUCTIONS_PAGE_SIZE);
  const legalAidsPaging = useDocumentPagination(LEGAL_AID_PAGE_SIZE);
  const authorshipsPaging = useDocumentPagination(AUTHORSHIPS_PAGE_SIZE);
  const teachingsPaging = useDocumentPagination(TEACHINGS_PAGE_SIZE);
  const dispensationsPaging = useDocumentPagination(DISPENSATIONS_PAGE_SIZE);
  const insurancePoliciesPaging = useDocumentPagination(
    INSURANCE_POLICIES_PAGE_SIZE,
  );

  const {
    data: courseCertificates,
    isLoading: isCourseCertificatesLoading,
    error: courseCertificatesError,
  } = useCourseCertificates({
    userId,
    page: courseCertificatesPaging.page,
    pageSize: courseCertificatesPaging.pageSize,
  });
  const {
    data: reductions,
    isLoading: isReductionsLoading,
    error: reductionsError,
  } = useReductions({
    userId,
    page: reductionsPaging.page,
    pageSize: reductionsPaging.pageSize,
  });
  const {
    data: legalAids,
    isLoading: isLegalAidsLoading,
    error: legalAidsError,
  } = useLegalAids({
    userId,
    page: legalAidsPaging.page,
    pageSize: legalAidsPaging.pageSize,
  });
  const {
    data: authorships,
    isLoading: isAuthorshipsLoading,
    error: authorshipsError,
  } = useAuthorships({
    userId,
    page: authorshipsPaging.page,
    pageSize: authorshipsPaging.pageSize,
  });
  const {
    data: teachings,
    isLoading: isTeachingsLoading,
    error: teachingsError,
  } = useTeachings({
    userId,
    page: teachingsPaging.page,
    pageSize: teachingsPaging.pageSize,
  });
  const {
    data: paabudList,
    isLoading: isPaabudLoading,
    error: paabudError,
  } = usePersonPaabudList({ personId: userId });
  const {
    data: paalaegList,
    isLoading: isPaalaegLoading,
    error: paalaegError,
  } = usePersonPaalaegList({ personId: userId });
  const {
    data: dispensations,
    isLoading: isDispensationsLoading,
    error: dispensationsError,
  } = useDispensations({
    userId,
    page: dispensationsPaging.page,
    pageSize: dispensationsPaging.pageSize,
  });
  const {
    data: insurancePolicies,
    isLoading: isInsurancePoliciesLoading,
    error: insurancePoliciesError,
  } = useInsurancePolicies({
    userId,
    page: insurancePoliciesPaging.page,
    pageSize: insurancePoliciesPaging.pageSize,
  });

  const visiblePaabud = useMemo(
    () => filterVisiblePaabud(paabudList ?? []),
    [paabudList],
  );

  const visiblePaalaeg = useMemo(
    () => filterVisiblePaalaeg(paalaegList ?? []),
    [paalaegList],
  );

  const periodLabelLookup = useMemo(
    () => buildPeriodLabelLookup(decoratedPeriods),
    [decoratedPeriods],
  );

  const hasParentContext =
    !!selectedPeriod || !!selectedPaabudId || !!selectedPaalaegId;

  const clearDocumentSelections = () => {
    setSelectedCourseCertificateId(undefined);
    setSelectedReductionId(undefined);
    setSelectedLegalAidId(undefined);
    setSelectedAuthorshipId(undefined);
    setSelectedTeachingId(undefined);
    setSelectedInsurancePolicyId(undefined);
  };

  const handleSelectPeriod = (period: DecoratedPersonEfteruddannelse) => {
    setSelectedPeriodId(period.id);
    setSelectedPaabudId(undefined);
    setSelectedPaalaegId(undefined);
    setSelectedDispensationId(undefined);
    clearDocumentSelections();
  };

  const handleSelectPaabud = (paabudId: string) => {
    setSelectedPaabudId(paabudId);
    setSelectedPeriodId(undefined);
    setSelectedPaalaegId(undefined);
    setSelectedDispensationId(undefined);
    clearDocumentSelections();
  };

  const handleSelectPaalaeg = (paalaegId: string) => {
    setSelectedPaalaegId(paalaegId);
    setSelectedPeriodId(undefined);
    setSelectedPaabudId(undefined);
    setSelectedDispensationId(undefined);
    clearDocumentSelections();
  };

  const handleSelectDispensation = (dispensationId: string) => {
    setSelectedDispensationId(dispensationId);
    setSelectedPeriodId(undefined);
    setSelectedPaabudId(undefined);
    setSelectedPaalaegId(undefined);
    clearDocumentSelections();
  };

  const handleBackToList = () => {
    setSelectedPeriodId(undefined);
    setSelectedPaabudId(undefined);
    setSelectedPaalaegId(undefined);
    setSelectedDispensationId(undefined);
    clearDocumentSelections();
  };

  const handleBackFromDocumentReview = () => {
    clearDocumentSelections();
  };

  const documentReviewBackHandler = hasParentContext
    ? handleBackFromDocumentReview
    : handleBackToList;

  if (checkResult) return checkResult;

  if (selectedCourseCertificateId) {
    return (
      <CourseCertificateReviewView
        userId={userId}
        evidenceId={selectedCourseCertificateId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedReductionId) {
    return (
      <ReductionReviewView
        userId={userId}
        reductionId={selectedReductionId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedLegalAidId) {
    return (
      <LegalAidReviewView
        userId={userId}
        legalAidId={selectedLegalAidId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedAuthorshipId) {
    return (
      <AuthorshipReviewView
        userId={userId}
        authorshipId={selectedAuthorshipId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedTeachingId) {
    return (
      <TeachingReviewView
        userId={userId}
        teachingId={selectedTeachingId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedInsurancePolicyId) {
    return (
      <InsurancePolicyReviewView
        userId={userId}
        insurancePolicyId={selectedInsurancePolicyId}
        onBack={documentReviewBackHandler}
      />
    );
  }

  if (selectedPeriod) {
    return (
      <EfteruddannelsePeriodDetailView
        userId={userId}
        period={selectedPeriod}
        allPeriods={decoratedPeriods}
        harForegaaendePeriode={Boolean(forrigePeriode)}
        lektionerIndberettetForrigePeriode={
          forrigePeriode?.lektionerGennemfoert
        }
        onSaved={() => void refetch()}
        onBack={handleBackToList}
        onSelectCertificate={setSelectedCourseCertificateId}
        onSelectReduction={setSelectedReductionId}
        onSelectLegalAid={setSelectedLegalAidId}
        onSelectAuthorship={setSelectedAuthorshipId}
        onSelectTeaching={setSelectedTeachingId}
      />
    );
  }

  if (selectedPaabudId) {
    return (
      <PaabudDetailView
        personId={userId}
        paabudId={selectedPaabudId}
        allPeriods={decoratedPeriods}
        onBack={handleBackToList}
        onSelectCertificate={setSelectedCourseCertificateId}
        onSelectReduction={setSelectedReductionId}
        onSelectLegalAid={setSelectedLegalAidId}
        onSelectAuthorship={setSelectedAuthorshipId}
        onSelectTeaching={setSelectedTeachingId}
      />
    );
  }

  if (selectedPaalaegId) {
    return (
      <PaalaegDetailView
        personId={userId}
        paalaegId={selectedPaalaegId}
        allPeriods={decoratedPeriods}
        onBack={handleBackToList}
        onSelectCertificate={setSelectedCourseCertificateId}
        onSelectReduction={setSelectedReductionId}
        onSelectLegalAid={setSelectedLegalAidId}
        onSelectAuthorship={setSelectedAuthorshipId}
        onSelectTeaching={setSelectedTeachingId}
      />
    );
  }

  if (selectedDispensationId) {
    return (
      <DispensationDetailView
        userId={userId}
        dispensationId={selectedDispensationId}
        periods={decoratedPeriods}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="container">
      <div className="c-card p-3 mb-4">
        <h2 className="h5 mb-3">Efteruddannelsesperioder</h2>
        <PeriodsTable
          periods={decoratedPeriods}
          isLoading={loading}
          onSelectPeriod={handleSelectPeriod}
        />
      </div>

      <AccordionRoot>
        <AccordionItem
          className="c-card c-card--accordion"
          value="paabud"
          isEmpty={!isPaabudLoading && !visiblePaabud.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Påbud ({visiblePaabud.length})</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <div className="c-card-action ">
              <CreatePaabudModal
                personId={userId}
                perioder={periodeIntervaller}
              />
            </div>
            <PaabudTable
              paabudList={visiblePaabud}
              isLoading={isPaabudLoading}
              error={paabudError}
              onSelectPaabud={handleSelectPaabud}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          className="c-card c-card--accordion"
          value="paalaeg"
          isEmpty={!isPaalaegLoading && !visiblePaalaeg.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Pålæg ({visiblePaalaeg.length})</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <div className="c-card-action ">
              <CreatePaalaegModal
                personId={userId}
                perioder={periodeIntervaller}
              />
            </div>
            <PaalaegTable
              paalaegList={visiblePaalaeg}
              isLoading={isPaalaegLoading}
              error={paalaegError}
              onSelectPaalaeg={handleSelectPaalaeg}
            />
          </AccordionContent>
        </AccordionItem>

        {documentCategories.map((category) => {
          const isCourseCertificates = category.id === "kursusbeviser";
          const isReductions = category.id === "reduktioner";
          const isLegalAid = category.id === "advokatvagt-retshjaelp";
          const isAuthorships = category.id === "forfatterskab";
          const isTeachings = category.id === "undervisning";
          const isInsurancePolicies = category.id === "forsikringspolicer";
          const documentCount = isCourseCertificates
            ? (courseCertificates?.total ?? 0)
            : isReductions
              ? (reductions?.total ?? 0)
              : isLegalAid
                ? (legalAids?.total ?? 0)
                : isAuthorships
                  ? (authorships?.total ?? 0)
                  : isTeachings
                    ? (teachings?.total ?? 0)
                    : isInsurancePolicies
                      ? (insurancePolicies?.total ?? 0)
                      : 0;

          return (
            <AccordionItem
              className="c-card c-card--accordion"
              value={category.id}
              isEmpty={!documentCount}
              key={category.id}
            >
              <AccordionHeader>
                <AccordionTrigger>
                  <span>
                    {category.title} ({documentCount})
                  </span>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent>
                <p className="text-muted">{category.description}</p>
                {isCourseCertificates ? (
                  <CourseCertificatesTable
                    certificates={courseCertificates?.items ?? []}
                    isLoading={isCourseCertificatesLoading}
                    error={courseCertificatesError}
                    onSelectCertificate={setSelectedCourseCertificateId}
                    {...courseCertificatesPaging.tableProps(courseCertificates)}
                  />
                ) : isReductions ? (
                  <ReductionsTable
                    reductions={reductions?.items ?? []}
                    isLoading={isReductionsLoading}
                    error={reductionsError}
                    onSelectReduction={setSelectedReductionId}
                    {...reductionsPaging.tableProps(reductions)}
                  />
                ) : isLegalAid ? (
                  <LegalAidTable
                    legalAids={legalAids?.items ?? []}
                    isLoading={isLegalAidsLoading}
                    error={legalAidsError}
                    onSelectLegalAid={setSelectedLegalAidId}
                    {...legalAidsPaging.tableProps(legalAids)}
                  />
                ) : isAuthorships ? (
                  <AuthorshipsTable
                    authorships={authorships?.items ?? []}
                    isLoading={isAuthorshipsLoading}
                    error={authorshipsError}
                    onSelectAuthorship={setSelectedAuthorshipId}
                    {...authorshipsPaging.tableProps(authorships)}
                  />
                ) : isInsurancePolicies ? (
                  <InsurancePoliciesTable
                    policies={insurancePolicies?.items ?? []}
                    isLoading={isInsurancePoliciesLoading}
                    error={insurancePoliciesError}
                    onSelectInsurancePolicy={setSelectedInsurancePolicyId}
                    {...insurancePoliciesPaging.tableProps(insurancePolicies)}
                  />
                ) : (
                  <TeachingsTable
                    teachings={teachings?.items ?? []}
                    isLoading={isTeachingsLoading}
                    error={teachingsError}
                    onSelectTeaching={setSelectedTeachingId}
                    {...teachingsPaging.tableProps(teachings)}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}

        <AccordionItem
          className="c-card c-card--accordion"
          value="dispensationer"
          isEmpty={!isDispensationsLoading && !(dispensations?.total ?? 0)}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Dispensationer ({dispensations?.total ?? 0})</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <div className="c-card-action ">
              <CreateDispensationModal
                personId={userId}
                periods={decoratedPeriods}
              />
            </div>
            <DispensationerTable
              dispensations={dispensations?.items ?? []}
              isLoading={isDispensationsLoading}
              error={dispensationsError}
              periodLabelLookup={periodLabelLookup}
              onSelectDispensation={handleSelectDispensation}
              {...dispensationsPaging.tableProps(dispensations)}
            />
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}
